import { useEffect, type ReactNode } from 'react';
import { Icon } from '../shell/Icon.tsx';
import type { Tone } from './state.ts';
import { useToasts, type Toast as ToastModel } from './toastStore.ts';
import styles from './Toast.module.css';

/* ==========================================================================
   Toast — the acknowledgement of an action the operator just took.

   Never used for incoming fleet state. A robot faulting is a change on the
   surface that shows robots; covering the corner of the screen with it costs
   the operator context and teaches them to dismiss without reading.
   ========================================================================== */

function ToastRow({ toast }: { toast: ToastModel }) {
  const dismiss = useToasts((s) => s.dismiss);
  const { id, ttl } = toast;

  useEffect(() => {
    if (ttl === undefined || ttl <= 0) return;
    const t = setTimeout(() => dismiss(id), ttl);
    return () => clearTimeout(t);
  }, [id, ttl, dismiss]);

  return (
    <div
      className={styles.toast}
      data-tone={toast.tone}
      role={toast.tone === 'error' ? 'alert' : 'status'}
    >
      <div className={styles.text}>
        <span className={styles.message}>{toast.message}</span>
        {toast.detail !== undefined && (
          <span className={styles.detail}>{toast.detail}</span>
        )}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss"
        onClick={() => dismiss(id)}
      >
        <Icon name="close" size={11} />
      </button>
    </div>
  );
}

/** Mount once, at the app root. */
export function ToastStack() {
  const toasts = useToasts((s) => s.toasts);
  if (toasts.length === 0) return null;
  return (
    <div className={styles.stack} aria-live="polite">
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} />
      ))}
    </div>
  );
}

/**
 * The same object rendered inside a panel body — for a result that belongs to
 * one surface and should not float over the whole console.
 */
export function InlineNotice({
  tone = 'neutral',
  children,
  detail,
  onDismiss,
}: {
  tone?: Tone | undefined;
  children: ReactNode;
  detail?: ReactNode;
  onDismiss?: (() => void) | undefined;
}) {
  return (
    <div className={styles.inline} data-tone={tone} role={tone === 'error' ? 'alert' : undefined}>
      <div className={styles.text}>
        <span className={styles.message}>{children}</span>
        {detail !== undefined && <span className={styles.detail}>{detail}</span>}
      </div>
      {onDismiss !== undefined && (
        <button type="button" className={styles.close} aria-label="Dismiss" onClick={onDismiss}>
          <Icon name="close" size={11} />
        </button>
      )}
    </div>
  );
}
